import React from 'react'
import { Table } from 'react-bootstrap'
import ScreenshotPlaceholder from '../../../components/ScreenshotPlaceholder'
import { Tip, Warn, Code, ArticleLink, Ext, TemplateBtn } from '../_helpers'

export const SECTION_ARTICLES = [
  {
    id: 'welcome',
    category: 'getting-started',
    title: 'Καλώς ήρθατε — πώς λειτουργεί η πλατφόρμα',
    summary: 'Μια γρήγορη περιήγηση στις δυνατότητες της πλατφόρμας και τα βήματα για να αποκτήσετε ένα αποτέλεσμα.',
    body: (
      <>
        <p>
          Η πλατφόρμα EcoEmpower σας βοηθά να σχεδιάσετε και να διαχειριστείτε την ενέργεια της
          κοινότητάς σας — προβλέποντας πόση ενέργεια θα καταναλώσουν τα κτίρια, βελτιστοποιώντας
          πότε να τη χρησιμοποιείτε και εκτιμώντας το κόστος και τα οφέλη επενδύσεων όπως
          ηλιακά πάνελ ή μπαταρίες.
        </p>
        <p>Η εργασία με την πλατφόρμα ακολουθεί πέντε απλά βήματα:</p>
        <ol>
          <li><strong>Δημιουργήστε μια κοινότητα</strong> — δώστε της όνομα, χώρα και νόμισμα.</li>
          <li><strong>Προσθέστε κτίρια και πόρους</strong> — τα κτίρια της κοινότητάς σας και τυχόν
            εξοπλισμό, όπως ηλιακά πάνελ, μπαταρίες ή φορτιστές EV.</li>
          <li><strong>Ανεβάστε δεδομένα</strong> — αρχεία ιστορικής ενέργειας (αρχεία CSV) που
            περιγράφουν πόση ενέργεια καταναλώνει κάθε κτίριο, πόση παράγει η ηλιακή/αιολική σας
            εγκατάσταση και τις τιμές ηλεκτρικής ενέργειας.</li>
          <li><strong>Εκτελέστε τα εργαλεία</strong> — Πρόβλεψη, Βελτιστοποίηση και Μακροπρόθεσμος Σχεδιασμός.</li>
          <li><strong>Δείτε τα αποτελέσματα</strong> — γραφήματα και αριθμούς που μπορείτε να
            αποθηκεύσετε και να εξαγάγετε.</li>
        </ol>
        <Tip>
          Τα εργαλεία λειτουργούν καλύτερα με πραγματικά δεδομένα, αλλά μπορείτε να εξερευνήσετε
          την πλατφόρμα πριν τα αποκτήσετε — τα περισσότερα εργαλεία χρησιμοποιούν ενδεικτικά
          δείγματα αποτελεσμάτων, οπότε τίποτα δεν σας εμποδίζει.
        </Tip>
        <ScreenshotPlaceholder
          img="dashboard-overview.png"
          description="The main Dashboard after logging in, showing the community count cards and the three action cards (Communities, Quick Start, Documentation)."
          source="Platform — log in and capture the main Dashboard (the page at the root URL, e.g. http://localhost/)."
          caption="Ο Πίνακας είναι το σημείο εκκίνησής σας."
        />
        <h5 className="mt-4">Πού εντάσσονται τα δεδομένα</h5>
        <p>
          Η πιο σημαντική — και πιο συχνή — εργασία είναι η σωστή μεταφόρτωση των αρχείων
          δεδομένων σας. Η ενότητα <strong>Προετοιμασία &amp; Μεταφόρτωση Δεδομένων</strong> αυτού
          του οδηγού εξηγεί ακριβώς πώς να το κάνετε, με έτοιμα πρότυπα. Εάν μια μεταφόρτωση
          απορριφθεί ποτέ, η ενότητα <strong>Αντιμετώπιση Προβλημάτων</strong> εξηγεί κάθε μήνυμα
          σφάλματος σε απλή γλώσσα.
        </p>
      </>
    ),
  },

  // =========================================================================
  {
    id: 'data-overview',
    category: 'data',
    title: 'Ποια αρχεία δεδομένων χρειάζομαι;',
    summary: 'Η πλήρης λίστα αρχείων δεδομένων, ο σκοπός καθενός και πότε το χρειάζεστε.',
    body: (
      <>
        <p>
          Τα δεδομένα μεταφορτώνονται ως <strong>αρχεία CSV</strong> — απλά αρχεία υπολογιστικών
          φύλλων που μπορείτε να δημιουργήσετε στο Excel ή στο Google Sheets. Υπάρχουν δύο κατηγορίες:
        </p>
        <ul>
          <li><strong>Δεδομένα κτιρίου</strong> — μεταφορτώνονται για συγκεκριμένο κτίριο (κατανάλωση
            ενέργειας, ηλιακά οροφής, λογαριασμοί).</li>
          <li><strong>Δεδομένα κοινότητας</strong> — μεταφορτώνονται μία φορά για ολόκληρη την
            κοινότητα (κοινόχρηστο ηλιακό πάρκο, ανεμογεννήτρια, τιμές ηλεκτρικής ενέργειας, καιρός).</li>
        </ul>

        <h5 className="mt-4">Αρχεία δεδομένων κτιρίου</h5>
        <Table bordered size="sm" responsive className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Αρχείο</th><th>Στήλες (απαιτούμενες)</th><th>Σκοπός</th></tr>
          </thead>
          <tbody>
            <tr><td>Προφίλ Φορτίου Κτιρίου</td><td><Code>timestamp</Code>, <Code>consumption_kwh</Code></td><td>Πόση ηλεκτρική ενέργεια καταναλώνει το κτίριο κάθε ώρα — απαραίτητο για πρόβλεψη και βελτιστοποίηση.</td></tr>
            <tr><td>Παραγωγή Φ/Β Κτιρίου</td><td><Code>timestamp</Code>, <Code>generation_kwh</Code></td><td>Πόση ενέργεια παρήγαγε το ηλιακό σύστημα οροφής του κτιρίου κάθε ώρα.</td></tr>
            <tr><td>Λογαριασμός Ενέργειας Κτιρίου</td><td><Code>period_start</Code>, <Code>period_end</Code>, <Code>consumption_kwh</Code>, <Code>cost_eur</Code></td><td>Μηνιαίοι λογαριασμοί — χρησιμοποιούνται για ανάλυση κόστους.</td></tr>
          </tbody>
        </Table>

        <h5 className="mt-4">Αρχεία δεδομένων κοινότητας</h5>
        <Table bordered size="sm" responsive className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Αρχείο</th><th>Στήλες (απαιτούμενες)</th><th>Σκοπός</th></tr>
          </thead>
          <tbody>
            <tr><td>Προφίλ Παραγωγής Φ/Β</td><td><Code>timestamp</Code>, <Code>generation_kw</Code></td><td>Παραγωγή κοινόχρηστου ηλιακού πάρκου.</td></tr>
            <tr><td>Προφίλ Παραγωγής Αιολικής</td><td><Code>timestamp</Code>, <Code>generation_kw</Code></td><td>Παραγωγή κοινοτικής ανεμογεννήτριας.</td></tr>
            <tr><td>Ιστορικό Τιμολόγιο Εισαγωγής</td><td><Code>timestamp</Code>, <Code>import_price_eur_kwh</Code></td><td>Η τιμή που πληρώσατε για αγορά ηλεκτρικής ενέργειας, ανά ώρα, για ένα έτος.</td></tr>
            <tr><td>Ιστορικό Τιμολόγιο Εξαγωγής</td><td><Code>timestamp</Code>, <Code>export_price_eur_kwh</Code></td><td>Η τιμή που λάβατε για πώληση ηλεκτρικής ενέργειας στο δίκτυο.</td></tr>
            <tr><td>Τιμολόγιο Εισαγωγής / Εξαγωγής Day-Ahead</td><td>ίδια με παραπάνω</td><td>Ωριαίες τιμές αυριανής ημέρας (24 γραμμές) — μεταφορτώνεται κάθε μέρα για βελτιστοποίηση Day-Ahead.</td></tr>
            <tr><td>Ηλιακός Καιρός</td><td><Code>timestamp</Code>, <Code>ghi_w_m2</Code></td><td>Ηλιακή ακτινοβολία και θερμοκρασία — βελτιώνει τις ηλιακές προβλέψεις.</td></tr>
            <tr><td>Αιολικός Καιρός</td><td><Code>timestamp</Code>, <Code>wind_speed_m_s</Code></td><td>Ταχύτητα ανέμου — βελτιώνει τις αιολικές προβλέψεις.</td></tr>
          </tbody>
        </Table>

        <h5 className="mt-4">Από πού προέρχονται αυτά τα δεδομένα;</h5>
        <p>Συνήθως δεν πληκτρολογείτε αυτά τα δεδομένα με το χέρι — τα εξάγετε από ένα σύστημα που
          ήδη χρησιμοποιείτε:</p>
        <ul>
          <li><strong>Κατανάλωση κτιρίου</strong> — από τη διαδικτυακή πύλη έξυπνου μετρητή του
            κτιρίου, από τον διαχειριστή του δικτύου διανομής (DSO) ή από τον λογαριασμό πελάτη
            του παρόχου ηλεκτρικής ενέργειας.</li>
          <li><strong>Παραγωγή ηλιακής / αιολικής ενέργειας</strong> — από την εφαρμογή παρακολούθησης
            ή τη διαδικτυακή πύλη του μετατροπέα ή της ανεμογεννήτριας (συνήθως ρυθμισμένη από
            τον εγκαταστάτη).</li>
          <li><strong>Τιμολόγια</strong> — από το ενεργειακό συμβόλαιο ή τον πάροχό σας· οι τιμές
            Day-Ahead προέρχονται από το εθνικό χρηματιστήριο ενέργειας ή, πάλι, τον πάροχό σας.</li>
          <li><strong>Καιρός</strong> — από εθνική μετεωρολογική υπηρεσία ή δωρεάν πάροχο
            μετεωρολογικών δεδομένων. Αυτό το αρχείο είναι προαιρετικό.</li>
        </ul>
        <p>
          Αυτά τα συστήματα συνήθως σας επιτρέπουν να εξαγάγετε ένα επιλεγμένο χρονικό διάστημα
          ως αρχείο CSV ή Excel. Μόλις έχετε αυτό το αρχείο, τα επόμενα δύο άρθρα σας δείχνουν
          πώς να το μορφοποιήσετε σωστά.
        </p>

        <Tip>
          Δεν χρειάζεστε <strong>όλα</strong> τα αρχεία για να ξεκινήσετε. Τα βασικά είναι ένα
          <strong> Προφίλ Φορτίου Κτιρίου</strong> για κάθε κτίριο και ένα αρχείο
          <strong> τιμολογίου</strong>. Προσθέστε αρχεία παραγωγής και καιρού αργότερα για
          βελτίωση της ακρίβειας.
        </Tip>
        <p className="mt-3">
          Κάθε τύπος αρχείου έχει το δικό του σύντομο άρθρο σε αυτή την ενότητα, με παράδειγμα
          και πρότυπο προς λήψη. Ξεκινήστε με το <em>«Δημιουργία σωστών αρχείων CSV»</em>
          παρακάτω — αποτρέπει τη μεγάλη πλειονότητα των προβλημάτων μεταφόρτωσης.
        </p>
      </>
    ),
  },

  // =========================================================================
  {
    id: 'data-csv-basics',
    category: 'data',
    title: 'Δημιουργία σωστών αρχείων CSV (Excel & Google Sheets)',
    summary: 'Οι βασικοί κανόνες για ένα έγκυρο αρχείο — και το νούμερο ένα λάθος που πρέπει να αποφύγετε.',
    body: (
      <>
        <p>
          Ένα <strong>αρχείο CSV</strong> είναι απλώς ένα υπολογιστικό φύλλο αποθηκευμένο σε απλή
          μορφή κειμένου. Το προετοιμάζετε στο Excel ή στο Google Sheets όπως οποιοδήποτε
          υπολογιστικό φύλλο, και έπειτα το αποθηκεύετε ως CSV. Ακολουθήστε αυτούς τους κανόνες
          και οι μεταφορτώσεις σας θα λειτουργούν από την πρώτη φορά.
        </p>

        <h5 className="mt-4">Οι βασικοί κανόνες</h5>
        <ol>
          <li><strong>Πρώτη γραμμή = επικεφαλίδες στηλών.</strong> Η πρώτη γραμμή πρέπει να
            περιέχει τα ακριβή ονόματα στηλών για τον τύπο αρχείου (για παράδειγμα{' '}
            <Code>timestamp</Code> και <Code>consumption_kwh</Code>). Η ορθογραφία και τα
            πεζά γράμματα πρέπει να ταιριάζουν ακριβώς.</li>
          <li><strong>Μία γραμμή ανά ώρα.</strong> Κάθε επόμενη γραμμή είναι μία ώρα δεδομένων.
            Ένα πλήρες έτος είναι <strong>8.760 γραμμές</strong> (365 ημέρες × 24 ώρες), συν
            την γραμμή επικεφαλίδας.</li>
          <li><strong>Χρησιμοποιήστε τη μορφή χρονοσφραγίδας</strong>{' '}
            <Code>2025-01-01T14:00:00Z</Code> — δηλαδή Έτος-Μήνας-Ημέρα, το γράμμα{' '}
            <Code>T</Code>, έπειτα Ώρα:Λεπτά:Δευτερόλεπτα, και τέλος το γράμμα <Code>Z</Code>.</li>
          <li><strong>Οι αριθμοί χρησιμοποιούν τελεία, ποτέ κόμμα.</strong> Γράψτε{' '}
            <Code>3.5</Code>, όχι <Code>3,5</Code>.</li>
          <li><strong>Χωρίς κενές γραμμές, χωρίς σύνολα, χωρίς επιπλέον σημειώσεις</strong> —
            μόνο η γραμμή επικεφαλίδας και οι γραμμές δεδομένων.</li>
        </ol>

        <Warn>
          <strong>Το πιο συνηθισμένο λάθος.</strong> Στην Ιταλία, τη Γαλλία, τη Γερμανία, την
          Τσεχία και την Ελλάδα, τα προγράμματα υπολογιστικών φύλλων γράφουν κανονικά τους
          δεκαδικούς αριθμούς με <strong>κόμμα</strong> (<Code>0,457</Code>). Η πλατφόρμα
          απαιτεί <strong>τελεία</strong> (<Code>0.457</Code>), οπότε ένα αρχείο με κόμματα
          απορρίπτεται με μήνυμα «Value must be a number». Αν το αρχείο σας έχει ήδη κόμματα,
          δεν χρειάζεται να το ξαναπληκτρολογήσετε — δείτε{' '}
          <ArticleLink to="data-fix-commas">Διόρθωση αρχείου που χρησιμοποιεί κόμματα για δεκαδικά</ArticleLink>.
        </Warn>

        <h5 className="mt-4">Σχετικά με τη στήλη χρονοσφραγίδας</h5>
        <p>
          Η πιο αξιόπιστη μορφή είναι <Code>2025-01-01T14:00:00Z</Code>, και έχει ένα κρυφό
          πλεονέκτημα: το Excel τείνει να επαναμορφοποιεί σιωπηλά ό,τι αναγνωρίζει ως ημερομηνία,
          αλλά συνήθως αφήνει αυτή τη μορφή ως έχει λόγω του <Code>T</Code> και του <Code>Z</Code>.
          Η πλατφόρμα κατανοεί επίσης συνήθεις ευρωπαϊκές μορφές όπως <Code>01/01/2025 14:00</Code>
          και <Code>2025-01-01</Code> — αλλά αυτές είναι ακριβώς αυτές που το Excel είναι πιθανό
          να αλλάξει στα κρυφά.
        </p>
        <p>
          Αν οι χρονοσφραγίδες σας έχουν αλλοιωθεί, ή χρειάζεται να δημιουργήσετε τη στήλη,
          βάλτε αυτό τον τύπο σε μια ελεύθερη στήλη (εδώ η αρχική ημερομηνία/ώρα βρίσκεται στο
          κελί <Code>A2</Code>):
        </p>
        <pre style={{ background: 'var(--color-neutral-50)', padding: 12, borderRadius: 6, fontSize: '0.8rem' }}>
{`=TEXT(A2,"yyyy-mm-ddThh:mm:ss")&"Z"`}
        </pre>
        <p>
          Αντιγράψτε τον σε κάθε γραμμή, έπειτα επιλέξτε τη στήλη και χρησιμοποιήστε
          <strong> Αντιγραφή → Ειδική Επικόλληση → Τιμές</strong> για να «κλειδώσετε» το
          αποτέλεσμα. Χρησιμοποιήστε αυτή τη στήλη ως <Code>timestamp</Code>.
        </p>

        <h5 className="mt-4">Πόσες γραμμές; (ανάλυση δεδομένων)</h5>
        <p>
          Μία γραμμή ανά χρονική περίοδο. Τα <strong>ωριαία</strong> δεδομένα είναι τυπικά —
          ένα πλήρες έτος είναι <strong>8.760 γραμμές</strong> — αλλά γίνονται δεκτά και δεδομένα
          <strong> 15 λεπτών</strong> και <strong>ημερήσια</strong>. Απαιτείται ελάχιστο 24 γραμμών
          (μία ημέρα).
        </p>

        <h5 className="mt-4">Αποθήκευση ως CSV από το Microsoft Excel</h5>
        <ol>
          <li>Κάντε κλικ στο <strong>Αρχείο → Αποθήκευση ως</strong>.</li>
          <li>Στο αναπτυσσόμενο μενού τύπου αρχείου επιλέξτε <strong>«CSV UTF-8 (Comma delimited) (*.csv)»</strong>.</li>
          <li>Κάντε κλικ στο <strong>Αποθήκευση</strong>. Αν το Excel προειδοποιήσει ότι ορισμένες
            λειτουργίες θα χαθούν, κάντε κλικ στο <strong>Ναι</strong> — αυτό είναι αναμενόμενο.</li>
        </ol>
        <p className="small text-muted">
          Αν η έκδοση Excel σας δεν περιλαμβάνει «CSV UTF-8», επιλέξτε αντ' αυτού «CSV (Comma delimited)».
        </p>
        <ScreenshotPlaceholder
          img="excel-save-as-csv.png"
          description="Excel's Save As dialog with the file-type dropdown open, highlighting the 'CSV UTF-8 (Comma delimited)' option."
          source="Microsoft Excel — open any workbook → File → Save As → click the file-type dropdown. Capture the dropdown open with the CSV UTF-8 entry highlighted."
          caption="Στο Excel, επιλέγετε πάντα «CSV UTF-8»."
        />

        <h5 className="mt-4">Αποθήκευση ως CSV από το Google Sheets</h5>
        <ol>
          <li>Κάντε κλικ στο <strong>Αρχείο → Λήψη → Τιμές διαχωρισμένες με κόμμα (.csv)</strong>.</li>
          <li>Το αρχείο αποθηκεύεται στον φάκελο Λήψεις του υπολογιστή σας.</li>
        </ol>
        <ScreenshotPlaceholder
          img="gsheets-download-csv.png"
          description="Google Sheets File → Download submenu, highlighting 'Comma-separated values (.csv)'."
          source="Google Sheets (sheets.google.com) — open any spreadsheet → File → Download. Capture the submenu open with 'Comma-separated values (.csv)' highlighted."
          caption="Στο Google Sheets, χρησιμοποιήστε Αρχείο → Λήψη → .csv."
        />

        <h5 className="mt-4">Ελέγξτε το αρχείο σας πριν από τη μεταφόρτωση</h5>
        <p>
          Ανοίξτε το αποθηκευμένο αρχείο <Code>.csv</Code> ξανά και επιβεβαιώστε τα παρακάτω.
          Για να είστε απολύτως σίγουροι, ανοίξτε το με ένα απλό πρόγραμμα επεξεργασίας κειμένου
          (<strong>Notepad</strong> στα Windows, <strong>TextEdit</strong> σε Mac) αντί για
          υπολογιστικό φύλλο — ένας επεξεργαστής κειμένου εμφανίζει τα πραγματικά περιεχόμενα
          του αρχείου, ενώ το Excel εφαρμόζει εκ νέου την τοπική μορφοποίηση και μπορεί να
          αποκρύψει το πραγματικό πρόβλημα.
        </p>
        <p>Επιβεβαιώστε ότι:</p>
        <ul>
          <li>Η πρώτη γραμμή εμφανίζει τα σωστά ονόματα στηλών.</li>
          <li>Οι αριθμοί μοιάζουν με <Code>3.5</Code> (τελεία), όχι <Code>3,5</Code> (κόμμα).</li>
          <li>Οι χρονοσφραγίδες μοιάζουν με <Code>2025-01-01T14:00:00Z</Code>.</li>
          <li>Δεν υπάρχουν κενές γραμμές ή γραμμές σύνοψης στο τέλος.</li>
        </ul>
        <Tip>
          Το πιο εύκολο: κατεβάστε ένα πρότυπο από οποιοδήποτε από τα άρθρα για συγκεκριμένους
          τύπους αρχείων σε αυτή την ενότητα, αντικαταστήστε τους παραδειγματικούς αριθμούς με
          τους δικούς σας και αποθηκεύστε. Το πρότυπο έχει ήδη τις σωστές επικεφαλίδες και τη
          σωστή μορφή αριθμών.
        </Tip>
      </>
    ),
  },

  // =========================================================================
  {
    id: 'data-fix-commas',
    category: 'data',
    title: 'Διόρθωση αρχείου που χρησιμοποιεί κόμματα για δεκαδικά',
    summary: 'Μετατρέψτε ένα υπάρχον αρχείο με αριθμούς με κόμμα (3,5) στη μορφή με τελεία που απαιτεί η πλατφόρμα (3.5) — χωρίς να ξαναπληκτρολογήσετε τίποτα.',
    body: (
      <>
        <p>
          Τα προγράμματα υπολογιστικών φύλλων στην Ιταλία, τη Γαλλία, τη Γερμανία, την Τσεχία
          και την Ελλάδα γράφουν κανονικά τους δεκαδικούς αριθμούς με <strong>κόμμα</strong> —
          <Code>0,457</Code> αντί για <Code>0.457</Code>. Η πλατφόρμα χρειάζεται τη μορφή με
          τελεία. Τα καλά νέα: <strong>δεν</strong> χρειάζεται να ξαναπληκτρολογήσετε τίποτα.
          Αλλάξτε μία ρύθμιση, αποθηκεύστε ξανά το αρχείο και είστε έτοιμοι. Χρησιμοποιήστε
          τη μέθοδο που ταιριάζει στο πρόγραμμα που έχετε.
        </p>

        <h5 className="mt-3">Μέθοδος 1 — Google Sheets (πιο εύκολη, δωρεάν)</h5>
        <ol>
          <li>Μεταβείτε στο <strong>sheets.google.com</strong> και ανοίξτε ένα κενό υπολογιστικό φύλλο.</li>
          <li>Επιλέξτε <strong>Αρχείο → Εισαγωγή → Μεταφόρτωση</strong> και επιλέξτε το αρχείο CSV σας.</li>
          <li>Επιλέξτε <strong>Αρχείο → Ρυθμίσεις</strong>. Στο πεδίο <strong>Τοπικές ρυθμίσεις</strong>,
            επιλέξτε <strong>Ηνωμένες Πολιτείες</strong> (ή Ηνωμένο Βασίλειο), έπειτα
            <strong> Αποθήκευση ρυθμίσεων</strong>. Αυτό ενημερώνει το Google Sheets να
            χρησιμοποιεί τελεία για τα δεκαδικά.</li>
          <li>Επιλέξτε <strong>Αρχείο → Λήψη → Τιμές διαχωρισμένες με κόμμα (.csv)</strong>.</li>
        </ol>
        <p>Το ληφθέν αρχείο χρησιμοποιεί πλέον τελείες και είναι έτοιμο για μεταφόρτωση.</p>
        <ScreenshotPlaceholder
          img="gsheets-locale-setting.png"
          description="Google Sheets — the File → Settings dialog with the Locale dropdown open and 'United States' highlighted."
          source="Google Sheets (sheets.google.com) — open any spreadsheet → File → Settings → the General tab. Capture the modal with the Locale dropdown open and 'United States' highlighted."
          caption="Ορίζοντας τις Τοπικές ρυθμίσεις σε Ηνωμένες Πολιτείες, το Google Sheets εξάγει τελείες."
        />

        <h5 className="mt-4">Μέθοδος 2 — Microsoft Excel</h5>
        <ol>
          <li>Ανοίξτε το αρχείο στο Excel, έπειτα μεταβείτε στο <strong>Αρχείο → Επιλογές → Για προχωρημένους</strong>.</li>
          <li>Στις <strong>Επιλογές επεξεργασίας</strong>, αποεπιλέξτε το <strong>«Χρήση διαχωριστικών συστήματος»</strong>.</li>
          <li>Ορίστε το <strong>Δεκαδικό διαχωριστικό</strong> σε τελεία <Code>.</Code> και
            το <strong>Διαχωριστικό χιλιάδων</strong> σε ένα κενό διάστημα (ή αφήστε το κενό).</li>
          <li>Κάντε κλικ στο <strong>ΟΚ</strong>, έπειτα χρησιμοποιήστε <strong>Αρχείο → Αποθήκευση ως</strong>
            και επιλέξτε <strong>«CSV UTF-8 (Comma delimited)»</strong>.</li>
        </ol>
        <ScreenshotPlaceholder
          img="excel-separator-options.png"
          description="Excel — File → Options → Advanced, the 'Editing options' area, with 'Use system separators' unticked and the decimal/thousands separator boxes visible."
          source="Microsoft Excel — open any workbook → File → Options → Advanced tab → scroll to 'Editing options'. Capture the section with the 'Use system separators' checkbox unticked."
          caption="Στο Excel, απενεργοποιήστε τα διαχωριστικά συστήματος και ορίστε το δεκαδικό διαχωριστικό σε τελεία."
        />
        <Tip>Αυτή η ρύθμιση επηρεάζει όλο το Excel. Μπορείτε να ενεργοποιήσετε ξανά το «Χρήση διαχωριστικών συστήματος» αργότερα.</Tip>

        <h5 className="mt-4">Μέθοδος 3 — Εύρεση &amp; Αντικατάσταση (τελευταία επιλογή)</h5>
        <p>
          Χρησιμοποιήστε αυτό μόνο αν οι αριθμοί είναι αποθηκευμένοι ως απλό κείμενο και το
          αρχείο <strong>δεν</strong> έχει διαχωριστικά χιλιάδων (τίποτα στη μορφή{' '}
          <Code>1.234,5</Code>), γιατί μια τυφλή αντικατάσταση θα κατέστρεφε και αυτά.
        </p>
        <ol>
          <li>Επιλέξτε μόνο τις στήλες που περιέχουν αριθμούς.</li>
          <li>Ανοίξτε το <strong>Εύρεση &amp; Αντικατάσταση</strong> (Ctrl+H), αναζητήστε{' '}
            <Code>,</Code>, αντικαταστήστε με <Code>.</Code>, έπειτα Αντικατάσταση όλων.</li>
          <li>Ελέγξτε μερικές τιμές πριν αποθηκεύσετε.</li>
        </ol>

        <h5 className="mt-4">Τι γίνεται με τα διαδικτυακά εργαλεία μετατροπής;</h5>
        <p>
          Υπάρχουν δωρεάν διαδικτυακοί ιστότοποι «μετατροπέα CSV», αλλά απαιτούν τη μεταφόρτωση
          του αρχείου σας σε ιστότοπο τρίτου, και τα ενεργειακά δεδομένα μπορεί να είναι
          ευαίσθητα. Συνιστούμε τις παραπάνω μεθόδους — χρησιμοποιούν λογισμικό που ήδη έχετε
          και διατηρούν το αρχείο στον υπολογιστή σας. Αν ο οργανισμός σας χρησιμοποιεί ήδη
          το Google Workspace, η Μέθοδος 1 παραμένει εντός εργαλείων που εμπιστεύεστε.
        </p>

        <Warn>
          Ένα αρχείο <Code>.csv</Code> ανοιγμένο στο Excel μπορεί να <em>φαίνεται</em> ότι έχει
          ακόμα κόμματα ακόμα και όταν αποθηκεύτηκε με τελείες, επειδή το Excel εφαρμόζει εκ
          νέου την τοπική σας μορφοποίηση στην οθόνη. Για να δείτε τι <strong>πραγματικά</strong>
          περιέχει το αρχείο, ανοίξτε το με ένα απλό πρόγραμμα επεξεργασίας κειμένου —
          <strong> Notepad</strong> στα Windows ή <strong>TextEdit</strong> σε Mac. Εκεί, οι
          αριθμοί πρέπει να εμφανίζονται ως <Code>3.5</Code>.
        </Warn>
      </>
    ),
  },
]
